import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Send, CheckCircle, Mail, Clock, Shield, MapPin, ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';
import { submitContactForm } from '../services/api';
import '../styles/Contact.css';
import SEO from '../components/SEO';

const Contact = () => {
  const [step, setStep] = useState(1);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasExistingSubmission, setHasExistingSubmission] = useState(false);
  const [showNewRequestOption, setShowNewRequestOption] = useState(false);
  
  // Assessment answers
  const [answers, setAnswers] = useState({
    concern: null,
    timeline: null,
    frustration: null
  });
  
  // Contact form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    contact_method: 'email',
    how_heard: '',
    additional_details: ''
  });

  // Check for existing submission on mount
  useEffect(() => {
    const existingSubmission = localStorage.getItem('cadence_contact_submitted');
    if (existingSubmission) {
      try {
        const data = JSON.parse(existingSubmission);
        // Check if submission was within last 7 days
        const submittedAt = new Date(data.submitted_at);
        const daysSince = (Date.now() - submittedAt.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince < 7) {
          setHasExistingSubmission(true);
          // Pre-fill form with previous data
          setFormData(prev => ({
            ...prev,
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            email: data.email || '',
            phone: data.phone || ''
          }));
        }
      } catch (e) {
        console.error('Error parsing existing submission:', e);
      }
    }
  }, []);

  const questions = [
    {
      id: 'concern',
      question: "What's your primary health concern?",
      options: [
        "Undiagnosed symptoms",
        "Second opinion on diagnosis",
        "Complex/chronic condition",
        "Navigating specialists",
        "Just exploring options"
      ]
    },
    {
      id: 'timeline',
      question: "How long have you been dealing with this?",
      options: [
        "Less than 3 months",
        "3-12 months",
        "1-3 years",
        "Over 3 years"
      ]
    },
    {
      id: 'frustration',
      question: "What frustrates you most?",
      options: [
        "Feeling dismissed by doctors",
        "No clear diagnosis",
        "Conflicting opinions",
        "Can't get appointments",
        "Don't know what to ask"
      ]
    }
  ];

  const totalSteps = questions.length + 1; // 3 questions + 1 contact info

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
    if (step < questions.length) {
      setStep(step + 1);
    } else {
      setStep(questions.length + 1); // Move to contact info step
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Build the message with structured data for admin
      const structuredMessage = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ASSESSMENT RESPONSES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Primary Concern: ${answers.concern}
Timeline: ${answers.timeline}
Main Frustration: ${answers.frustration}
${formData.additional_details ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ADDITIONAL DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${formData.additional_details}` : ''}
`.trim();

      await submitContactForm({
        ...formData,
        assessment_concern: answers.concern,
        assessment_timeline: answers.timeline,
        assessment_frustration: answers.frustration,
        service_interest: 'Consultation Request',
        message: structuredMessage
      });
      
      // Save submission to localStorage
      localStorage.setItem('cadence_contact_submitted', JSON.stringify({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        concern: answers.concern,
        submitted_at: new Date().toISOString()
      }));
      
      setFormSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  // Rotating empathetic messages for success screen
  const successMessages = [
    {
      headline: "We Hear You.",
      subtext: "Navigating the healthcare system shouldn't feel like a battle. A member of our team will reach out to you shortly to discuss how we can help."
    },
    {
      headline: "You're Not Alone.",
      subtext: "We understand the frustration of feeling dismissed or not getting answers. Someone from CADENCE will contact you via your preferred method within 24 hours."
    },
    {
      headline: "Your Health Journey Matters.",
      subtext: "We're here to be your advocates. Expect a personal call or email from our team soon to learn more about your needs."
    },
    {
      headline: "Finally, Someone in Your Corner.",
      subtext: "We take the time to listen. A CADENCE team member will be reaching out shortly to schedule your consultation."
    },
    {
      headline: "Clarity is Coming.",
      subtext: "You've taken the first step toward getting the answers you deserve. We'll help you ask the questions that haven't been asked yet."
    }
  ];

  // Select a random message based on submission time
  const getSuccessMessage = () => {
    const index = Math.floor(Date.now() / 1000) % successMessages.length;
    return successMessages[index];
  };

  const successMessage = getSuccessMessage();

  // Success screen
  if (formSubmitted) {
    return (
      <div className="contact-page">
        <div className="contact-success-wrapper">
          <div className="contact-success-box">
            <div className="success-icon-wrapper">
              <CheckCircle size={48} className="success-icon" />
            </div>
            <h2 className="success-headline">{successMessage.headline}</h2>
            <p className="success-message">{successMessage.subtext}</p>
            
            <div className="success-details-card">
              <div className="detail-row">
                <span className="detail-label">Your Concern:</span>
                <span className="detail-value">{answers.concern}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">How Long:</span>
                <span className="detail-value">{answers.timeline}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Contact Via:</span>
                <span className="detail-value highlight">{formData.contact_method === 'phone' ? 'Phone Call' : 'Email'} to {formData.contact_method === 'phone' ? formData.phone : formData.email}</span>
              </div>
            </div>

            <div className="success-footer">
              <p className="footer-note">
                Questions? Email us at <a href="mailto:cadence@cadencemedicalconsulting.com">cadence@cadencemedicalconsulting.com</a>
              </p>
              <Link to="/" className="back-to-home-btn">
                <ArrowLeft size={18} />
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <SEO
        title="Contact CADENCE — Start Your Medical Expert Opinion"
        description="Reach out to CADENCE Medical Consulting for a confidential consultation. We respond within 24-48 business hours. Veteran-owned, HIPAA-protected."
        path="/contact"
      />
      <div className="contact-slide-wrapper">
        <div className="contact-slide-box">
          
          {/* Back to home link */}
          <Link to="/" className="contact-back-link">
            <ArrowLeft size={18} />
            Back to Home
          </Link>

          {/* Existing submission acknowledgment */}
          {hasExistingSubmission && !showNewRequestOption && step === 1 && (
            <div className="existing-submission-banner">
              <AlertCircle size={20} />
              <div className="banner-content">
                <strong>Welcome Back!</strong>
                <p>We received your consultation request recently. We'll be in touch soon!</p>
                <div className="banner-actions">
                  <button 
                    className="banner-btn secondary"
                    onClick={() => setShowNewRequestOption(true)}
                  >
                    Submit a Different Request
                  </button>
                  <Link to="/" className="banner-btn primary">
                    Return Home
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Progress indicator */}
          {(!hasExistingSubmission || showNewRequestOption) && (
            <div className="contact-progress">
              <div className="progress-dots">
                {[...Array(totalSteps)].map((_, idx) => (
                  <span 
                    key={idx} 
                    className={`progress-dot ${idx < step ? 'filled' : ''} ${idx === step - 1 ? 'active' : ''}`}
                  />
                ))}
              </div>
              <span className="progress-text">
                Step {step} of {totalSteps}
              </span>
            </div>
          )}

          {/* Question slides (steps 1-3) */}
          {(!hasExistingSubmission || showNewRequestOption) && step <= questions.length && (
            <div className="contact-question-slide">
              <h2 className="slide-question">{questions[step - 1].question}</h2>
              
              <div className="slide-options">
                {questions[step - 1].options.map((option, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`slide-option ${answers[questions[step - 1].id] === option ? 'selected' : ''}`}
                    onClick={() => handleAnswer(questions[step - 1].id, option)}
                    data-testid={`option-${idx}`}
                  >
                    <span className="option-text">{option}</span>
                    <ArrowRight size={18} className="option-arrow" />
                  </button>
                ))}
              </div>

              {step > 1 && (
                <button type="button" className="slide-back-btn" onClick={goBack}>
                  <ArrowLeft size={16} />
                  Previous Question
                </button>
              )}
            </div>
          )}

          {/* Contact info slide (step 4) */}
          {(!hasExistingSubmission || showNewRequestOption) && step === questions.length + 1 && (
            <div className="contact-info-slide">
              <h2 className="slide-question">Almost There! How Can We Reach&nbsp;You?</h2>
              <p className="slide-subtitle">We'll contact you within 48 hours to discuss your&nbsp;needs.</p>
              
              <form className="contact-slide-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input 
                      type="text" 
                      name="first_name"
                      required 
                      placeholder="First name" 
                      value={formData.first_name}
                      onChange={handleInputChange}
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input 
                      type="text" 
                      name="last_name"
                      required 
                      placeholder="Last name" 
                      value={formData.last_name}
                      onChange={handleInputChange}
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Email Address *</label>
                  <input 
                    type="email" 
                    name="email"
                    required 
                    placeholder="your.email@example.com" 
                    value={formData.email}
                    onChange={handleInputChange}
                    data-testid="email-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input 
                    type="tel" 
                    name="phone"
                    required 
                    placeholder="(555) 123-4567" 
                    value={formData.phone}
                    onChange={handleInputChange}
                    data-testid="phone-input"
                  />
                </div>

                <div className="form-group">
                  <label>Preferred Contact Method</label>
                  <div className="contact-method-options">
                    <label className={`method-option ${formData.contact_method === 'email' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="contact_method"
                        value="email"
                        checked={formData.contact_method === 'email'}
                        onChange={handleInputChange}
                      />
                      <Mail size={16} />
                      Email
                    </label>
                    <label className={`method-option ${formData.contact_method === 'phone' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="contact_method"
                        value="phone"
                        checked={formData.contact_method === 'phone'}
                        onChange={handleInputChange}
                      />
                      <Clock size={16} />
                      Phone
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>How did you hear about us?</label>
                  <select 
                    name="how_heard"
                    value={formData.how_heard}
                    onChange={handleInputChange}
                    data-testid="how-heard-select"
                  >
                    <option value="">Select an option...</option>
                    <option value="search">Online Search</option>
                    <option value="social">Social Media</option>
                    <option value="referral">Friend/Family Referral</option>
                    <option value="doctor">Healthcare Provider</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Tell Us More <span className="optional-label">(Optional)</span></label>
                  <textarea
                    name="additional_details"
                    value={formData.additional_details}
                    onChange={handleInputChange}
                    placeholder="Feel free to share any additional details about your situation, specific questions you'd like answered, or anything else that would help us prepare for your consultation..."
                    rows={4}
                    data-testid="additional-details-input"
                    className="additional-details-textarea"
                  />
                  <span className="field-hint">This helps us tailor your 15-minute consultation to your specific needs.</span>
                </div>

                <div className="consent-checkbox">
                  <input type="checkbox" id="consent" required />
                  <label htmlFor="consent">
                    I consent to CADENCE Medical Consulting, LLC contacting me. My information will be kept confidential per HIPAA&nbsp;guidelines.
                  </label>
                </div>

                <div className="form-actions">
                  <button type="button" className="slide-back-btn" onClick={goBack}>
                    <ArrowLeft size={16} />
                    Back
                  </button>
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={isSubmitting}
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : 'Send My Request'}
                    <Send size={18} />
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Side info */}
          <div className="contact-side-info">
            <div className="side-info-item">
              <Clock size={20} />
              <div>
                <strong>Response Time</strong>
                <span>Within 48 Hours</span>
              </div>
            </div>
            <div className="side-info-item">
              <MapPin size={20} />
              <div>
                <strong>Service Area</strong>
                <span>DE, MD & SC (Telehealth)</span>
              </div>
            </div>
            <div className="side-info-item">
              <Shield size={20} />
              <div>
                <strong>HIPAA Compliant</strong>
                <span>Your info is protected</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Contact;
